import { isCancelled } from '@seek/ca-http-client';
import type {
  SearchParams,
  SearchResultJobV5,
  SearchResultLocationSuggestionV5,
  SearchResultLocationV5,
} from '@seek/chalice-types';
import { normaliseSearchQuery } from '@seek/seek-jobs-seo';
import { addJobDetailsMetadata, addTags } from '@seek/sol-js';
import get from 'lodash/get';

import isSuburb from 'src/modules/locations';
import { logger } from 'src/modules/logger';
import pixelPromise from 'src/modules/pixel-promise';
import { searchResultsViewModel } from 'src/modules/refine-job-search';
import { searchResultsPageRegex } from 'src/modules/routes-regexp';
import { hashToUserQueryId } from 'src/modules/seek-jobs-analytics';
import { SUBMIT_SEARCH, UPDATE_WHERE_FIELD } from 'src/store/search/types';

import { selectExperimentsAsSolTags } from '../experiments/experimentHelpers';
import { LOCATION_CHANGED } from '../location/types';
import { updateBaseKeywordParam } from '../search';
import {
  selectAuthenticated,
  selectNewSince,
  selectRequestId,
  selectStateToken,
} from '../selectors';
import type { ChaliceStore, TypedAction, TypedThunkAction } from '../types';

import { fetchCount } from './fetchCount';
import { fetchSearchResults } from './fetchSearchResults';
import { getDecodedTokenWithoutIndex } from './results.token';
import { getIsFeatured } from './resultsHelpers';
import {
  ADD_HIDDEN_JOB,
  ADD_QUERY_HINTS,
  CLEAR_DYNAMIC_PILLS,
  CLEAR_HIDDEN_JOBS,
  CLEAR_NEW_SINCE,
  CLEAR_RESULTS,
  CLEAR_RESULTS_MOBILE_VIEW,
  GET_COUNT_NEW_SUCCESS,
  GET_RESULTS_BEGIN,
  GET_RESULTS_ERROR,
  GET_RESULTS_ERROR_CANCELLATION,
  GET_RESULTS_SUCCESS_V5,
  JORA_RESULT_LINK_CLICKED,
  MORE_OPTIONS,
  REMOVE_HIDDEN_JOB,
  REMOVE_QUERY_HINTS,
  RESULT_LINK_CLICKED,
  SAVE_SOL_REFERENCES,
  SAVE_SOL_REFERENCES_FOR_RECS,
  SET_SELECTED_JOB_ID,
  SET_TITLE_TAG_SHORT_LOCATION_NAME,
  SET_TITLE_TAG_TEST_TITLE,
  UPDATE_SEARCH_SOURCE,
  type GetResultsSuccessActionV5,
  type RelatedSearch,
  type ResultsState,
  type SaveSolReferencesAction,
  type SaveSolReferencesForRecsAction,
  type SearchViewModel,
  type SetSelectedJobIdAction,
  type SetTitleTagShortLocationName,
  type SetTitleTagTestTitle,
  type SolReferenceKeyLookup,
} from './types';

const EMPTY_RELATED_SEARCH: RelatedSearch[] = [];

export const SELECTED_JOB_ID_SEARCH_QUERY_KEY = 'jobId';

export const initialState: ResultsState = {
  companySuggestion: undefined,
  facets: {},
  hasLoadedCountNew: false,
  hidden: false,
  hiddenJobs: [],
  isError: false,
  isLoading: false,
  jobIds: [],
  lastPage: 0,
  locationWhere: null,
  relatedSearches: EMPTY_RELATED_SEARCH,
  results: null,
  searchSource: '',
  shouldIncludeQueryHints: false, // there's no query hints at the start
  solReferenceKeys: [],
  sortMode: null,
  source: '',
  title: '',
  titleTagShortLocationName: '',
  titleTagTestTitle: '',
  totalCount: null,
  totalCountNewToYou: null,
  totalPages: 0,
};

export interface CalculateResultsArgs {
  location?: SearchResultLocationV5;
  locationSuggestions?: SearchResultLocationSuggestionV5[];
  query?: SearchParams | any;
  results?: SearchResultJobV5[] | null;
  totalCount?: number;
}

const calculateResults = (args: CalculateResultsArgs = {}): SearchViewModel => {
  const {
    query = {},
    results = null,
    location,
    totalCount,
    locationSuggestions,
  } = args;
  const page = query.page || '1';

  const { jobs, ...viewModel } = searchResultsViewModel({
    page,
    results,
    location,
    totalCount,
    locationSuggestions,
    searchParams: query,
  }) as SearchViewModel;

  return {
    ...viewModel,
    jobs: jobs && jobs.length > 0 ? jobs : null,
  };
};

export default function reducer(
  state: ResultsState = initialState,
  action: TypedAction,
): ResultsState {
  switch (action.type) {
    case MORE_OPTIONS: {
      return {
        ...state,
        hidden: true,
      };
    }

    case REMOVE_QUERY_HINTS: {
      return {
        ...state,
        shouldIncludeQueryHints: false,
      };
    }

    case ADD_QUERY_HINTS: {
      return {
        ...state,
        shouldIncludeQueryHints: true,
      };
    }

    case CLEAR_NEW_SINCE: {
      return {
        ...state,
        newSince: undefined,
      };
    }

    case UPDATE_WHERE_FIELD: {
      return {
        ...state,
        shouldIncludeQueryHints: state.isQueryModified
          ? false
          : state.shouldIncludeQueryHints,
      };
    }

    case CLEAR_DYNAMIC_PILLS: {
      return {
        ...state,
        pills: [],
      };
    }

    case CLEAR_RESULTS_MOBILE_VIEW: {
      return {
        ...state,
        jobIds: [],
        source: '',
        isError: false,
        companySuggestion: undefined,
        relatedSearches: EMPTY_RELATED_SEARCH,
        hidden: false,
      };
    }

    case CLEAR_RESULTS:
    case GET_RESULTS_BEGIN: {
      return {
        ...state,
        jobIds: [],
        source: '',
        isLoading: true,
        isError: false,
        companySuggestion: undefined,
        relatedSearches: EMPTY_RELATED_SEARCH,
        hidden: false,
      };
    }

    case GET_COUNT_NEW_SUCCESS: {
      const { totalCountNewToYou } = action.payload;
      return {
        ...state,
        totalCountNewToYou,
        hasLoadedCountNew: true,
      };
    }

    case SUBMIT_SEARCH: {
      return {
        ...state,
        isLoading: true,
        solReferenceKeys: [],
        titleTagShortLocationName: '', // Reset server rendered short location name when submit search in client to prevent showing wrong location description
        titleTagTestTitle: '', // Reset server rendered test title when submit search in client to prevent showing wrong test title
        searchSource: '', // Reset results of showing searchSource
      };
    }

    case LOCATION_CHANGED: {
      const { pathname, query } = action.payload.location;
      const isSearchResultPage = searchResultsPageRegex.test(pathname || '');
      const isHomepage = pathname === '/';
      const homePageUpdates = isHomepage
        ? {
            title: '', // Use default localised title for home page
            results: null, // Reset results to get a fresh list when returning to SERP
          }
        : {};
      return {
        ...state,
        ...homePageUpdates,
        selectedJobId: isSearchResultPage
          ? query?.[SELECTED_JOB_ID_SEARCH_QUERY_KEY]
          : undefined,
      };
    }

    case UPDATE_SEARCH_SOURCE: {
      const { searchSource } = action.payload;
      return {
        ...state,
        searchSource,
      };
    }

    case GET_RESULTS_SUCCESS_V5: {
      const { query, currentPageNumber, userQueryId, relatedSearches } =
        action.payload;

      const {
        canonicalCompany,
        data,
        facets,
        gptTargeting,
        info,
        intentSuggestions,
        isQueryModified,
        location,
        searchExecuted,
        solMetadata,
        sortModes = null,
        totalCount = 0,
        totalCountNewToYou = 0,
      } = action.payload.result;

      const suggestions = action.payload.result.suggestions;

      const locationSuggestions = suggestions?.location;
      const companySuggestion = suggestions?.company;
      const pills = suggestions?.pills;
      const asyncPillsToken = suggestions?.asyncPillsToken;

      const locationWhere =
        location === undefined ||
        location.description === 'All Australia' ||
        location.description === 'All New Zealand'
          ? ''
          : location.description;

      const tracking = get(action.payload, 'result.data[0].tracking');
      const uniqueSearchToken = getDecodedTokenWithoutIndex(tracking);
      const { source, newSince } = info || {};
      const solMetadataString = JSON.stringify(solMetadata || {});
      const isRadialFilterShown = isSuburb(location);
      const isRadialFilterNudgeShown =
        currentPageNumber === 2 &&
        totalCount >= 50 &&
        isRadialFilterShown &&
        !query.hasOwnProperty('distance');
      const jobIds = data
        ? data.map((job: SearchResultJobV5) => `${job.id}`)
        : [];

      return {
        ...state,
        asyncPillsToken,
        canonicalCompany,
        companySuggestion,
        facets,
        gptTargeting,
        intentSuggestions,
        isError: false,
        isLoading: false,
        isQueryModified,
        isRadialFilterNudgeShown,
        isRadialFilterShown,
        jobIds,
        location,
        locationWhere,
        newSince,
        pills,
        relatedSearches,
        results: calculateResults({
          query,
          results: data,
          location,
          totalCount,
          locationSuggestions,
        }),
        searchExecuted,
        solMetadata,
        solMetadataString,
        sortMode: sortModes,
        source,
        totalCount,
        totalCountNewToYou,
        uniqueSearchToken,
        userQueryId,
      };
    }

    case GET_RESULTS_ERROR: {
      return {
        ...state,
        results: calculateResults(),
        jobIds: [],
        source: '',
        isLoading: false,
        isError: true,
        title: 'Error loading results',
        lastPage: undefined,
        relatedSearches: EMPTY_RELATED_SEARCH,
      };
    }

    case SAVE_SOL_REFERENCES: {
      const jobs = state.results?.jobs || [];

      const refs = !ENV.SERVER
        ? addJobDetailsMetadata(jobs).map<SolReferenceKeyLookup>(
            (key, index) => {
              const { id, displayType, ...job } = jobs[index];
              const isFeatured = job.isFeatured;
              return {
                hash: key,
                isFeaturedOrDisplayKeyType: getIsFeatured({
                  displayType,
                  isFeatured,
                }),
                id,
              };
            },
          )
        : [];
      return {
        ...state,
        solReferenceKeys: refs,
      };
    }

    case SAVE_SOL_REFERENCES_FOR_RECS: {
      const { recommendations, isFeaturedOrDisplayKeyType } = action.payload;
      const jobs = recommendations?.map(({ job, solMetadata }) => ({
        job,
        solMetadata: {
          ...solMetadata,
          jobId: job.id,
        },
      }));

      const generateRefs = () =>
        addJobDetailsMetadata(jobs).map<SolReferenceKeyLookup>((key, index) => {
          const {
            job: { id },
          } = jobs[index];
          return {
            hash: key,
            isFeaturedOrDisplayKeyType,
            id,
          };
        });

      const refs = !ENV.SERVER ? generateRefs() : [];
      const shouldKeepCurrentRefs =
        isFeaturedOrDisplayKeyType === 'competitivePlacement';

      return {
        ...state,
        solReferenceKeys: [
          ...(shouldKeepCurrentRefs ? state.solReferenceKeys : []),
          ...refs,
        ],
      };
    }

    case SET_SELECTED_JOB_ID: {
      const { jobId } = action.payload;
      return {
        ...state,
        selectedJobId: jobId,
      };
    }

    case SET_TITLE_TAG_SHORT_LOCATION_NAME: {
      const { titleTagShortLocationName } = action.payload;
      return {
        ...state,
        titleTagShortLocationName,
      };
    }

    case SET_TITLE_TAG_TEST_TITLE: {
      const { titleTagTestTitle } = action.payload;
      return {
        ...state,
        titleTagTestTitle,
      };
    }

    case ADD_HIDDEN_JOB: {
      const { jobId } = action.payload;
      return {
        ...state,
        hiddenJobs: [...(state.hiddenJobs ?? []), jobId],
      };
    }
    case REMOVE_HIDDEN_JOB: {
      const { jobId } = action.payload;
      return {
        ...state,
        hiddenJobs: state.hiddenJobs?.filter((id) => id !== jobId),
      };
    }

    case CLEAR_HIDDEN_JOBS: {
      return {
        ...state,
        hiddenJobs: [],
      };
    }

    default: {
      return state;
    }
  }
}

export const moreOptions = (): TypedAction => ({
  type: MORE_OPTIONS,
  meta: {
    hotjar: 'More Options Clicked',
  },
});

export const getResults =
  ({
    brand,
    country,
    zone,
    path,
    query,
    cookies,
    userAgent,
    visitorId,
    serverProps,
    isSQCEnabled = false,
  }: any): TypedThunkAction<Promise<void>> =>
  (dispatch, getState) => {
    const state = getState();
    const normalisedQuery = normaliseSearchQuery({ path, query });

    // SSA: If doing 'new to you' search on server block and redo
    // on client to prevent server/client mismatch
    if (ENV.SERVER && normalisedQuery.tags === 'new') {
      return Promise.resolve();
    }

    const userQueryId = hashToUserQueryId({ query: normalisedQuery });
    const isAuthenticated = selectAuthenticated(state);
    const newSince = selectNewSince(state);
    const stateToken = selectStateToken(state);
    const requestId = selectRequestId(state);

    dispatch({
      type: GET_RESULTS_BEGIN,
      payload: {
        query: normalisedQuery,
      },
    });

    const searchBaseArgs = {
      apiQuery: normalisedQuery,
      brand,
      cookies,
      country,
      isAuthenticated,
      newSince,
      requestId,
      serverProps,
      stateToken,
      userAgent,
      userQueryId,
      visitorId,
      zone,
      isSQCEnabled,
    };

    const fetchSearchResultsPromise =
      searchBaseArgs.apiQuery.tags === 'new' && !state.user.authenticated
        ? Promise.resolve(null)
        : fetchSearchResults({
            ...searchBaseArgs,
            serverProps,
            relatedSearchesCount: 12,
          })(dispatch, getState, null);

    // SSA Do not do a count request for new to you if we are on the server
    const fetchCountNew = ENV.SERVER
      ? Promise.resolve(-1)
      : fetchCount({
          ...searchBaseArgs,
          serverProps,
          relatedSearchesCount: 12,
          apiQuery: {
            ...normalisedQuery,
            tags: 'new',
          },
        })(dispatch, getState, null);

    const fetchCountAll = fetchCount({
      ...searchBaseArgs,
      serverProps,
      apiQuery: {
        ...normalisedQuery,
        tags: null,
      },
    })(dispatch, getState, null);

    const fetchData = Promise.all([
      fetchSearchResultsPromise,
      fetchCountAll,
      fetchCountNew,
    ]);

    return fetchData
      .then(([result, fetchCountAllResult, fetchCountNewResult]) => {
        const currentPageNumber = getState().location.pageNumber;

        const experimentTags = selectExperimentsAsSolTags(getState());
        const mappedResult = {
          ...result,
          data: result
            ? result.data.map((jobData) => ({
                ...jobData,
                branding: jobData?.branding,
                solMetadata: addTags(jobData.solMetadata, experimentTags),
              }))
            : [],
        };

        const relatedSearches = mappedResult.suggestions?.relatedSearches
          ? mappedResult.suggestions.relatedSearches
          : [];
        const locationSuggestions = mappedResult.suggestions?.location
          ? mappedResult.suggestions.location
          : [];

        // For SQC, where baseKeywords need to be updated with the corrected keywords
        // to maintain proper functionality of dynamic pills suggestions as
        // they rely on building upon the existing baseKeywords.
        const isQueryModified = result?.isQueryModified;
        const searchExecutedKeywords = result?.searchExecuted?.keywords;
        if (isQueryModified && searchExecutedKeywords) {
          dispatch(updateBaseKeywordParam(searchExecutedKeywords || ''));
        }

        const action: GetResultsSuccessActionV5 = {
          type: GET_RESULTS_SUCCESS_V5,
          payload: {
            query: normalisedQuery,
            result: {
              ...mappedResult,
              totalCount: fetchCountAllResult,
              totalCountNewToYou: fetchCountNewResult,
              // ensure the experiments are added to solmetadata
              solMetadata: {
                ...addTags(mappedResult.solMetadata, {
                  ...experimentTags,
                }),
                query: normalisedQuery,
              },
            },
            currentPageNumber,
            userQueryId,
            relatedSearches,
            locationSuggestions,
          },
        };
        dispatch(action);
      })
      .catch((err) => {
        if (isCancelled(err)) {
          dispatch({
            type: GET_RESULTS_ERROR_CANCELLATION,
            error: true,
          });
        }
        logger.error(err, 'fetchData promise was cancelled');
        throw err;
      })
      .catch((err) => {
        dispatch({
          type: GET_RESULTS_ERROR,
          error: true,
          payload: err,
        });
        logger.error(err, 'Failed to fetch search data');
      });
  };

export const resultLinkClicked =
  (job: any): TypedThunkAction =>
  (dispatch) => {
    const { joraClickTrackingUrl } = job;

    dispatch({
      type: RESULT_LINK_CLICKED,
    });

    if (joraClickTrackingUrl) {
      dispatch({
        type: JORA_RESULT_LINK_CLICKED,
      });

      return pixelPromise(joraClickTrackingUrl);
    }
    return null;
  };

export const saveSolReferencesAction = (): SaveSolReferencesAction => ({
  type: SAVE_SOL_REFERENCES,
});

export const saveSolReferencesForRecsAction = (
  recommendations: SaveSolReferencesForRecsAction['payload']['recommendations'],
  isFeaturedOrDisplayKeyType: SaveSolReferencesForRecsAction['payload']['isFeaturedOrDisplayKeyType'],
): SaveSolReferencesForRecsAction => ({
  type: SAVE_SOL_REFERENCES_FOR_RECS,
  payload: { recommendations, isFeaturedOrDisplayKeyType },
});

export const setSelectedJobIdAction = (
  jobId: SetSelectedJobIdAction['payload']['jobId'],
): SetSelectedJobIdAction => ({
  type: SET_SELECTED_JOB_ID,
  payload: { jobId },
});

export const clearResults = (): TypedAction => ({
  type: CLEAR_RESULTS,
});

export const clearResultsMobileView = (): TypedAction => ({
  type: CLEAR_RESULTS_MOBILE_VIEW,
});

export const clearDynamicPills = (): TypedAction => ({
  type: CLEAR_DYNAMIC_PILLS,
});

export const clearNewSince = (): TypedAction => ({
  type: CLEAR_NEW_SINCE,
});

export const createJobLinkSolReferenceSelector =
  ({
    jobId,
    isFeaturedOrDisplayKeyType,
  }: {
    jobId: SolReferenceKeyLookup['id'];
    isFeaturedOrDisplayKeyType: SolReferenceKeyLookup['isFeaturedOrDisplayKeyType'];
  }) =>
  (state: ChaliceStore) => {
    const match = state.results.solReferenceKeys.find(
      (ref) =>
        ref.id === jobId &&
        ref.isFeaturedOrDisplayKeyType === isFeaturedOrDisplayKeyType,
    );
    return match?.hash;
  };
export const createBatchJobLinkSolReferenceSelector =
  (
    propsArray?: Array<{
      jobId: SolReferenceKeyLookup['id'];
      isFeaturedOrDisplayKeyType: SolReferenceKeyLookup['isFeaturedOrDisplayKeyType'];
    }>,
  ) =>
  (state: ChaliceStore) =>
    propsArray?.map(({ jobId, isFeaturedOrDisplayKeyType }) => {
      const match = state.results?.solReferenceKeys?.find(
        (ref) =>
          ref.id === jobId &&
          ref.isFeaturedOrDisplayKeyType === isFeaturedOrDisplayKeyType,
      );
      return match?.hash || '';
    }) ?? [];
export const setTitleTagShortLocationName = (
  shortLocationName: string,
): SetTitleTagShortLocationName => ({
  type: SET_TITLE_TAG_SHORT_LOCATION_NAME,
  payload: { titleTagShortLocationName: shortLocationName },
});

export const setTitleTagTestTitle = (
  titleTagTestTitle: string,
): SetTitleTagTestTitle => ({
  type: SET_TITLE_TAG_TEST_TITLE,
  payload: { titleTagTestTitle },
});

export const updateSearchSource = (searchSource: string): TypedAction => ({
  type: UPDATE_SEARCH_SOURCE,
  payload: { searchSource },
});

export const removeQueryHintsParam = (): TypedAction => ({
  type: 'REMOVE_QUERY_HINTS',
});

export const addQueryHintsParam = (): TypedAction => ({
  type: 'ADD_QUERY_HINTS',
});

export const addHiddenJob = (jobId: string): TypedAction => ({
  type: ADD_HIDDEN_JOB,
  payload: { jobId },
});

export const removeHiddenJob = (jobId: string): TypedAction => ({
  type: REMOVE_HIDDEN_JOB,
  payload: { jobId },
});

export const clearHiddenJobs = (): TypedAction => ({
  type: CLEAR_HIDDEN_JOBS,
});

export { fetchSearchResults as fetchSearchResultsForTest };
